import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';

import { getPblCases, getPblDashboard, getPblVettingApplications } from '@/api/pbl.api';
import { formatActualMeetingDuration, formatMeetingDateRange } from '@/components/Pbl/CaseProgressDetail/helpers';
import PageContainer from '@/components/Container/PageContainer';
import { PblCaseListContent } from '@/components/Pbl/CaseList';
import { Link as LinkIcon } from '@/components/Icon';
import type {
  PblCaseListColumn,
  PblCaseListFilter,
  PblCaseListRow,
  PblCaseListStatus,
} from '@/components/Pbl/CaseList/types';
import useResize from '@/hooks/useResize';
import useTableRequestState from '@/hooks/useTableRequestState';
import { getAuthSelector } from '@/redux/auth/auth.slice';
import { useAppSelector } from '@/redux/store.hook';
import PATH from '@/router/path';
import type { PblDashboard, PblLawyerProfile } from '@/types/pbl';
import { toHongKongMeetingDisplayDateTime } from '@/utils/pblDate';
import { translatePblDashboardLabel } from '@/utils/pblDisplayTranslation';
import { errorHandler } from '@/utils/toastHandler';
import {
  getPblOcManageTableCases,
  mapPblCaseToOcManagedCase,
  mapPblCaseToOcPendingReviewCase,
} from '@/pages/Pbl/OcManageCases/caseReviewState';
import type { PblOcManagedCase, PblOcManagedCaseStatus } from '@/pages/Pbl/OcManageCases/types';
import {
  isCitizenManagedCaseDetailAccessible,
  mapPblCasesToCitizenManagedCases,
} from '@/pages/Pbl/CitizenManageCases/citizenCaseState';
import { isLawyerCurrentCase, isPendingLawyerConfirmationCase } from '@/pages/Pbl/LawyerManageCases/caseVisibility';
import {
  excludeHiddenPblVettingApplications,
  getPblVettingProfileCreatedAt,
  getPblVettingProfileReference,
  getPblVettingProfileStatus,
} from '@/pages/Pbl/VettingDashboard/vettingDashboardState';
import { formatPblEnglishChineseName } from '@/utils/pblDisplay';

import {
  ActionButton,
  CardGrid,
  ContentPanel,
  DashboardLayout,
  EmptyText,
  ItemBody,
  ItemList,
  ItemMeta,
  ItemTitle,
  MainStack,
  SectionHeader,
  SummaryCard,
  SummaryCardLink,
  SummaryLabel,
  SummaryValue,
  StorageProgress,
  StorageUsageHeader,
  StorageUsageList,
  StorageUsageName,
  StorageUsageValue,
  StorageWarning,
  TitleBar,
} from './styles';
import { getPblDashboardCardPath } from './dashboardCardNavigation';
import { useTranslation } from 'react-i18next';

const buildCaseDetailPath = (caseId: number | string) => PATH.pbl.caseDetail.replace(':caseId', String(caseId));

const buildLawyerMeetingCalendarPath = (caseId: number | string) =>
  PATH.pbl.lawyerMeetingCalendar.replace(':caseId', String(caseId));

const isOcDashboardRole = (role?: PblDashboard['role']) =>
  role === 'oc_admin' || role === 'oc_staff' || role === 'pbl_admin';

const isLawyerDashboardRole = (role?: PblDashboard['role']) => role === 'pbl_lawyer';

const isCitizenDashboardRole = (role?: PblDashboard['role']) => role === 'citizen';

const isReviewerDashboardRole = (role?: PblDashboard['role']) => role === 'pbl_vetting_reviewer';

const isPendingLawyerTimeslotCase = (item: PblOcManagedCase) => item.backendStatus === 'pending_lawyer_timeslot';

const formatStorageBytes = (bytes: number) => `${(Math.max(0, bytes) / 1024 / 1024).toFixed(1)} MB`;

const mapVettingApplicationToReviewRow = (application: PblLawyerProfile): PblCaseListRow => ({
  id: application.id,
  caseId: getPblVettingProfileReference(application),
  backendStatus: getPblVettingProfileStatus(application),
  applicantName: formatPblEnglishChineseName(
    application.full_name_en,
    application.full_name_zh,
    application.full_name_en
  ),
  legalServiceNeeds: '-',
  creationDate: getPblVettingProfileCreatedAt(application),
  status: 'pending',
});

const PblRoleDashboard = () => {
  //   @ts-ignore
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userData } = useAppSelector(getAuthSelector);
  const { isMobile, isTablet } = useResize();
  const [dashboard, setDashboard] = useState<PblDashboard | null>(null);
  const [caseRows, setCaseRows] = useState<PblOcManagedCase[]>([]);
  const [caseTotalEntries, setCaseTotalEntries] = useState(0);
  const [caseCurrentPage, setCaseCurrentPage] = useState(1);
  const [caseRowsPerPage, setCaseRowsPerPage] = useState(10);
  const [caseLastPage, setCaseLastPage] = useState(1);
  const [reviewRows, setReviewRows] = useState<PblCaseListRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const caseRequest = useTableRequestState(caseRows.length > 0);
  const reviewRequest = useTableRequestState(reviewRows.length > 0);
  const caseStatusLabels = useMemo<Record<PblOcManagedCaseStatus, string>>(
    () => ({
      in_progress: t('pbl.pagesHardcoded.roleDashboard.inProgress'),
      to_be_rated: t('pbl.pagesHardcoded.roleDashboard.toBeRated'),
      case_closed: t('pbl.pagesHardcoded.roleDashboard.caseClosed'),
      case_cancelled: t('pbl.pagesHardcoded.roleDashboard.caseCancelled'),
      to_be_confirmed: t('pbl.dashboardCalendarNotifications.dashboard.caseTable.statuses.toBeConfirmed'),
    }),
    [t]
  );
  const ocCaseFilters = useMemo<Array<{ value: PblCaseListFilter; label: string }>>(
    () => [
      { value: 'all', label: t('pbl.pagesHardcoded.roleDashboard.all') },
      { value: 'in_progress', label: t('pbl.pagesHardcoded.roleDashboard.inProgress') },
      { value: 'to_be_rated', label: t('pbl.pagesHardcoded.roleDashboard.toBeRated') },
      { value: 'case_closed', label: t('pbl.pagesHardcoded.roleDashboard.caseClosed') },
    ],
    [t]
  );
  const citizenCaseColumns = useMemo<PblCaseListColumn[]>(
    () => [
      { key: 'caseId', label: t('pbl.pagesHardcoded.roleDashboard.caseId') },
      { key: 'enquiryCategory', label: t('pbl.pagesHardcoded.roleDashboard.enquiryCategory') },
      { key: 'applicantName', label: t('pbl.pagesHardcoded.roleDashboard.applicantName') },
      { key: 'creationDate', label: t('pbl.pagesHardcoded.roleDashboard.creationDate') },
      { key: 'status', label: t('pbl.pagesHardcoded.roleDashboard.status') },
    ],
    [t]
  );
  const citizenCaseStatusLabels = useMemo<Record<PblCaseListStatus, string>>(
    () => ({
      pending: t('pbl.dashboardCalendarNotifications.dashboard.caseTable.statuses.pending'),
      in_progress: t('pbl.pagesHardcoded.roleDashboard.inProgress'),
      to_be_rated: t('pbl.pagesHardcoded.roleDashboard.toBeRated'),
      case_closed: t('pbl.pagesHardcoded.roleDashboard.caseClosed'),
      case_cancelled: t('pbl.pagesHardcoded.roleDashboard.caseCancelled'),
      to_be_confirmed: t('pbl.pagesHardcoded.roleDashboard.toBeVerified'),
    }),
    [t]
  );
  const citizenCaseFilters = useMemo<Array<{ value: PblCaseListFilter; label: string }>>(
    () => [
      { value: 'all', label: t('pbl.pagesHardcoded.roleDashboard.all') },
      { value: 'to_be_confirmed', label: t('pbl.pagesHardcoded.roleDashboard.toBeVerified') },
      { value: 'in_progress', label: t('pbl.pagesHardcoded.roleDashboard.inProgress') },
      { value: 'to_be_rated', label: t('pbl.pagesHardcoded.roleDashboard.toBeRated') },
      { value: 'case_closed', label: t('pbl.pagesHardcoded.roleDashboard.caseClosed') },
      { value: 'case_cancelled', label: t('pbl.pagesHardcoded.roleDashboard.caseCancelled') },
    ],
    [t]
  );
  const reviewerQueueColumns = useMemo<PblCaseListColumn[]>(
    () => [
      {
        key: 'caseId',
        label: t('pbl.pagesHardcoded.roleDashboard.reviewQueue.applicationReference'),
      },
      {
        key: 'applicantName',
        label: t('pbl.pagesHardcoded.vettingDashboard.lawyerName'),
      },
      {
        key: 'creationDate',
        label: t('pbl.pagesHardcoded.vettingDashboard.creationDate'),
      },
      {
        key: 'status',
        label: t('pbl.pagesHardcoded.vettingDashboard.status'),
      },
    ],
    [t]
  );

  const loadDashboard = useCallback(async () => {
    setIsLoading(true);

    try {
      const response = await getPblDashboard();

      if (response.data.success === false) {
        errorHandler(response.data.message || t('pbl.pagesHardcoded.roleDashboard.failedToLoadPblDashboard'));
        return;
      }

      setDashboard(response.data.data);
    } catch (error: any) {
      errorHandler(
        error.response?.data?.message ||
          error.response?.data?.data ||
          t('pbl.pagesHardcoded.roleDashboard.failedToLoadPblDashboard')
      );
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const isOcDashboard = isOcDashboardRole(dashboard?.role);
  const isLawyerDashboard = isLawyerDashboardRole(dashboard?.role);
  const isCitizenDashboard = isCitizenDashboardRole(dashboard?.role);
  const isReviewerDashboard = isReviewerDashboardRole(dashboard?.role);
  const shouldShowCaseTable = isOcDashboard || isLawyerDashboard || isCitizenDashboard;
  const shouldShowReviewQueue = isOcDashboard || isLawyerDashboard || isReviewerDashboard;
  const tableRows = useMemo(
    () => (isOcDashboard ? getPblOcManageTableCases(caseRows) : caseRows),
    [caseRows, isOcDashboard]
  );

  useEffect(() => {
    if (!shouldShowCaseTable) {
      setCaseRows([]);
      setCaseTotalEntries(0);
      setCaseLastPage(1);
      return;
    }

    const requestId = caseRequest.beginRequest();
    const reviewRequestId = isLawyerDashboard ? reviewRequest.beginRequest() : null;

    const requestParams = isCitizenDashboard
      ? { per_page: caseRowsPerPage, page: caseCurrentPage }
      : { per_page: 50, page: 1 };

    getPblCases(requestParams)
      .then((response) => {
        if (response.data.success === false) {
          const message = response.data.message || t('pbl.pagesHardcoded.roleDashboard.failedToLoadCases');
          if (caseRequest.failRequest(requestId, message)) errorHandler(message);
          if (reviewRequestId !== null) reviewRequest.failRequest(reviewRequestId, message);
          return;
        }

        if (!caseRequest.completeRequest(requestId)) return;
        if (reviewRequestId !== null) reviewRequest.completeRequest(reviewRequestId);

        const page = response.data.data;

        if (isCitizenDashboard) {
          setCaseRows(mapPblCasesToCitizenManagedCases(page.data));
          setCaseTotalEntries(page.total);
          setCaseLastPage(page.last_page || 1);
          return;
        }

        if (isLawyerDashboard) {
          const tableCases = page.data.filter(isLawyerCurrentCase);
          const pendingCases = page.data.filter((item) => isPendingLawyerConfirmationCase(item, userData));
          setCaseRows(tableCases.map(mapPblCaseToOcManagedCase));
          setCaseTotalEntries(tableCases.length);
          setCaseLastPage(1);
          setReviewRows(pendingCases.map(mapPblCaseToOcPendingReviewCase));
          return;
        }

        setCaseRows(page.data.map(mapPblCaseToOcManagedCase));
        setCaseTotalEntries(page.data.length);
        setCaseLastPage(1);
      })
      .catch((error: any) => {
        const message =
          error.response?.data?.message ||
          error.response?.data?.data ||
          t('pbl.pagesHardcoded.roleDashboard.failedToLoadCases');
        if (caseRequest.failRequest(requestId, message)) errorHandler(message);
        if (reviewRequestId !== null) reviewRequest.failRequest(reviewRequestId, message);
      });

    return () => {
      caseRequest.cancelRequest(requestId);
      if (reviewRequestId !== null) reviewRequest.cancelRequest(reviewRequestId);
    };
  }, [
    caseCurrentPage,
    caseRequest.beginRequest,
    caseRequest.cancelRequest,
    caseRequest.completeRequest,
    caseRequest.failRequest,
    caseRequest.retryKey,
    caseRowsPerPage,
    isCitizenDashboard,
    isLawyerDashboard,
    reviewRequest.beginRequest,
    reviewRequest.cancelRequest,
    reviewRequest.completeRequest,
    reviewRequest.failRequest,
    shouldShowCaseTable,
    t,
    userData,
  ]);

  useEffect(() => {
    if (!isOcDashboard && !isReviewerDashboard) return;

    const requestId = reviewRequest.beginRequest();

    const loadReviewRows = async () => {
      try {
        if (isReviewerDashboard) {
          const response = await getPblVettingApplications({ status: 'pending_review', per_page: 50, page: 1 });

          if (response.data.success === false) {
            const message = response.data.message || t('pbl.pagesHardcoded.roleDashboard.reviewQueue.failedToLoad');
            if (reviewRequest.failRequest(requestId, message)) errorHandler(message);
            return;
          }

          if (!reviewRequest.completeRequest(requestId)) return;

          setReviewRows(
            excludeHiddenPblVettingApplications(response.data.data.data).map(mapVettingApplicationToReviewRow)
          );
          return;
        }

        const response = await getPblCases({ status: 'pending_oc_review', per_page: 50, page: 1 });

        if (response.data.success === false) {
          const message = response.data.message || t('pbl.pagesHardcoded.roleDashboard.reviewQueue.failedToLoad');
          if (reviewRequest.failRequest(requestId, message)) errorHandler(message);
          return;
        }

        if (!reviewRequest.completeRequest(requestId)) return;

        setReviewRows(response.data.data.data.map(mapPblCaseToOcPendingReviewCase));
      } catch (error: any) {
        const message =
          error.response?.data?.message ||
          error.response?.data?.data ||
          t('pbl.pagesHardcoded.roleDashboard.reviewQueue.failedToLoad');
        if (reviewRequest.failRequest(requestId, message)) errorHandler(message);
      }
    };

    loadReviewRows();

    return () => {
      reviewRequest.cancelRequest(requestId);
    };
  }, [
    isOcDashboard,
    isReviewerDashboard,
    reviewRequest.beginRequest,
    reviewRequest.cancelRequest,
    reviewRequest.completeRequest,
    reviewRequest.failRequest,
    reviewRequest.retryKey,
    t,
  ]);

  const changeCitizenRowsPerPage = (nextRowsPerPage: number) => {
    setCaseRowsPerPage(nextRowsPerPage);
    setCaseCurrentPage(1);
  };

  const openCase = (item: PblOcManagedCase) => {
    if (isCitizenDashboard) {
      if (isCitizenManagedCaseDetailAccessible(item)) {
        navigate(buildCaseDetailPath(item.id));
      }
      return;
    }

    if (isLawyerDashboard) {
      if (isPendingLawyerTimeslotCase(item)) {
        navigate(buildLawyerMeetingCalendarPath(item.id));
        return;
      }

      navigate(buildCaseDetailPath(item.id));
      return;
    }

    if (item.backendStatus === 'pending_oc_review') {
      navigate(PATH.pbl.pendingReviewDetail.replace(':caseId', String(item.id)));
      return;
    }

    navigate(buildCaseDetailPath(item.id));
  };

  const isCaseRowClickable = (item: PblOcManagedCase) =>
    isCitizenDashboard ? isCitizenManagedCaseDetailAccessible(item) : true;

  const reviewQueueTitle = isLawyerDashboard
    ? t('pbl.pagesHardcoded.roleDashboard.reviewQueue.lawyerTitle')
    : isReviewerDashboard
    ? t('pbl.pagesHardcoded.roleDashboard.reviewQueue.reviewerTitle')
    : t('pbl.pagesHardcoded.roleDashboard.reviewQueue.ocTitle');

  const reviewQueueSearchLabel = isReviewerDashboard
    ? t('pbl.pagesHardcoded.roleDashboard.reviewQueue.searchApplications')
    : t('pbl.pagesHardcoded.roleDashboard.reviewQueue.searchCases');

  const openReviewRow = (item: PblCaseListRow) => {
    if (isReviewerDashboard) {
      navigate(PATH.pbl.vettingDetail.replace(':profileId', String(item.id)));
      return;
    }

    if (isLawyerDashboard) {
      navigate(PATH.pbl.lawyerPendingReviewDetail.replace(':caseId', String(item.id)));
      return;
    }

    navigate(PATH.pbl.pendingReviewDetail.replace(':caseId', String(item.id)));
  };

  return (
    <PageContainer
      title={
        <TitleBar isMobile={isMobile}>
          <span>
            {t('pbl.pagesHardcoded.roleDashboard.home')}
            <strong>{t('pbl.pagesHardcoded.roleDashboard.pblDashboard')}</strong>
          </span>
        </TitleBar>
      }
    >
      <DashboardLayout isTablet={isTablet}>
        <MainStack>
          <ContentPanel>
            <SectionHeader>{t('pbl.pagesHardcoded.roleDashboard.summary')}</SectionHeader>
            {isLoading && !dashboard ? (
              <EmptyText>{t('pbl.pagesHardcoded.roleDashboard.loadingDashboard')}</EmptyText>
            ) : null}
            <CardGrid>
              {(dashboard?.summary_cards || []).map((card) => {
                const label = translatePblDashboardLabel(t, card);
                const destination = dashboard ? getPblDashboardCardPath(dashboard.role, card.key) : null;

                return (
                  <SummaryCard key={card.key} variant={card.variant}>
                    {destination ? (
                      <SummaryCardLink to={destination} aria-label={`${label}: ${card.value}`} title={label}>
                        <LinkIcon.LinkSVG aria-hidden="true" focusable="false" />
                        <SummaryValue>{card.value}</SummaryValue>
                        <SummaryLabel>{label}</SummaryLabel>
                      </SummaryCardLink>
                    ) : (
                      <>
                        <SummaryValue>{card.value}</SummaryValue>
                        <SummaryLabel>{label}</SummaryLabel>
                      </>
                    )}
                  </SummaryCard>
                );
              })}
            </CardGrid>
            {!isLoading && dashboard?.summary_cards.length === 0 ? (
              <EmptyText>{t('pbl.pagesHardcoded.roleDashboard.noSummaryAvailable')}</EmptyText>
            ) : null}
          </ContentPanel>

          {(dashboard?.office_storage_usage?.length || 0) > 0 ? (
            <ContentPanel>
              <SectionHeader>
                {t('pbl.pagesHardcoded.roleDashboard.officeStorageUsage', {
                  defaultValue: 'Office storage usage',
                })}
              </SectionHeader>
              <StorageUsageList>
                {dashboard?.office_storage_usage?.map((usage) => {
                  const progressPercentage = Math.min(100, Math.max(0, usage.percentage));

                  return (
                    <div key={usage.pbl_oc_office_id}>
                      <StorageUsageHeader>
                        <StorageUsageName>{usage.office_name}</StorageUsageName>
                        <StorageUsageValue>
                          {formatStorageBytes(usage.used_bytes)} / {formatStorageBytes(usage.quota_bytes)} (
                          {usage.percentage.toFixed(1)}%)
                        </StorageUsageValue>
                      </StorageUsageHeader>
                      <StorageProgress
                        $exceeded={usage.is_exceeded}
                        role="progressbar"
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={Math.round(progressPercentage)}
                        style={{ '--usage-percentage': `${progressPercentage}%` } as CSSProperties}
                      >
                        <span />
                      </StorageProgress>
                      {usage.is_exceeded ? (
                        <StorageWarning>
                          {t('pbl.pagesHardcoded.roleDashboard.officeStorageExceeded', {
                            defaultValue:
                              'Storage quota exceeded. Existing services remain available while additional storage is arranged.',
                          })}
                        </StorageWarning>
                      ) : null}
                    </div>
                  );
                })}
              </StorageUsageList>
            </ContentPanel>
          ) : null}

          {isLawyerDashboardRole(dashboard?.role) ? (
            <ContentPanel>
              <SectionHeader>{t('pbl.pagesHardcoded.roleDashboard.totalConsultationTime')}</SectionHeader>
              <ItemMeta>
                {t('pbl.caseDetailDocuments.meeting.table.actualSharedTime')}: {formatActualMeetingDuration(dashboard?.lawyer_total_duration?.seconds) ?? t('pbl.caseDetailDocuments.meeting.table.durationUnavailable')}
                {' · '}
                {t('pbl.pagesHardcoded.roleDashboard.unavailableMeetingCount', {
                  count: dashboard?.lawyer_total_duration?.unavailable_meetings ?? 0,
                })}
                <br />
                {t('pbl.caseDetailDocuments.meeting.table.actualElapsedTime')}: {formatActualMeetingDuration(dashboard?.lawyer_total_duration?.meeting_seconds) ?? t('pbl.caseDetailDocuments.meeting.table.durationUnavailable')}
                {' · '}{t('pbl.pagesHardcoded.roleDashboard.unavailableMeetingCount', {
                  count: dashboard?.lawyer_total_duration?.unavailable_meeting_durations ?? 0,
                })}
              </ItemMeta>
            </ContentPanel>
          ) : null}

          <ContentPanel>
            <SectionHeader>{t('pbl.pagesHardcoded.roleDashboard.upcomingMeetings')}</SectionHeader>
            <ItemList>
              {(dashboard?.upcoming_meetings || []).map((meeting) => (
                <li key={meeting.id}>
                  <ItemBody>
                    <ItemTitle>{meeting.display_title || t('pbl.pagesHardcoded.roleDashboard.videoMeeting')}</ItemTitle>
                    <ItemMeta style={{ whiteSpace: 'pre-line' }}>
                      {meeting.case_id ||
                        t('pbl.pagesHardcoded.roleDashboard.caseValue', { value1: meeting.case_internal_id })}
                      {' | '}
                      {formatMeetingDateRange(
                        toHongKongMeetingDisplayDateTime(meeting.scheduled_at, meeting.scheduled_at_display),
                        toHongKongMeetingDisplayDateTime(meeting.ends_at, meeting.ends_at_display)
                      )}
                    </ItemMeta>
                  </ItemBody>
                  <ActionButton type="button" onClick={() => navigate(buildCaseDetailPath(meeting.case_internal_id))}>
                    {t('pbl.common.labels.case', { defaultValue: 'Case' })}
                  </ActionButton>
                </li>
              ))}
            </ItemList>
            {!isLoading && dashboard?.upcoming_meetings.length === 0 ? (
              <EmptyText>{t('pbl.pagesHardcoded.roleDashboard.noUpcomingMeetings')}</EmptyText>
            ) : null}
          </ContentPanel>

          {shouldShowReviewQueue ? (
            <ContentPanel>
              <SectionHeader>{reviewQueueTitle}</SectionHeader>
              <PblCaseListContent
                rows={reviewRows}
                columns={isReviewerDashboard ? reviewerQueueColumns : undefined}
                mobileCardLayout
                tabletCardLayout
                embedded
                stripedRows
                searchAriaLabel={reviewQueueSearchLabel}
                requestState={reviewRequest.requestState}
                onRetry={reviewRequest.retry}
                onRowClick={openReviewRow}
              />
            </ContentPanel>
          ) : null}

          {shouldShowCaseTable ? (
            <ContentPanel>
              <SectionHeader>{t('pbl.pagesHardcoded.roleDashboard.cases')}</SectionHeader>
              <PblCaseListContent
                rows={tableRows}
                mobileCardLayout
                tabletCardLayout
                embedded
                stripedRows
                columns={isCitizenDashboard ? citizenCaseColumns : undefined}
                filters={isCitizenDashboard ? citizenCaseFilters : ocCaseFilters}
                totalEntries={isCitizenDashboard ? caseTotalEntries : tableRows.length}
                currentPage={isCitizenDashboard ? caseCurrentPage : undefined}
                rowsPerPage={isCitizenDashboard ? caseRowsPerPage : undefined}
                lastPage={isCitizenDashboard ? caseLastPage : undefined}
                onPageChange={isCitizenDashboard ? setCaseCurrentPage : undefined}
                onRowsPerPageChange={isCitizenDashboard ? changeCitizenRowsPerPage : undefined}
                searchAriaLabel={
                  isCitizenDashboard
                    ? t('pbl.pagesHardcoded.roleDashboard.searchCitizenCases')
                    : t('pbl.pagesHardcoded.roleDashboard.searchCases')
                }
                requestState={caseRequest.requestState}
                onRetry={caseRequest.retry}
                statusLabels={isCitizenDashboard ? citizenCaseStatusLabels : caseStatusLabels}
                isRowClickable={isCaseRowClickable}
                onRowClick={openCase}
              />
            </ContentPanel>
          ) : null}
        </MainStack>
      </DashboardLayout>
    </PageContainer>
  );
};

export default PblRoleDashboard;
